import React, { useState, useRef, useEffect } from 'react';

/**
 * SearchableSelect - A searchable dropdown component
 * Supports paste, instant search filtering, and keyboard navigation
 */
function SearchableSelect({ value, options, onChange, placeholder = 'Search...' }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Find the display label for current value
    const selectedOption = options.find(opt => String(opt.value) === String(value));
    const displayValue = selectedOption ? selectedOption.label : '';

    // Filter options based on search term (instant filtering)
    const filteredOptions = options.filter(opt => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            String(opt.label).toLowerCase().includes(searchLower) ||
            String(opt.value).toLowerCase().includes(searchLower)
        );
    });

    // Handle outside click to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset highlighted index when filtered options change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [searchTerm]);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        setIsOpen(true);
    };

    const handleInputFocus = () => {
        setIsOpen(true);
        setSearchTerm('');
    };

    const handleSelect = (optionValue) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setSearchTerm('');
        setIsOpen(false);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                setIsOpen(true);
                e.preventDefault();
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex].value);
                } else if (filteredOptions.length === 1) {
                    handleSelect(filteredOptions[0].value);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    return (
        <div className="searchable-select" ref={containerRef}>
            <div className="searchable-select__input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    className="form-control searchable-select__input"
                    value={isOpen ? searchTerm : displayValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={value ? displayValue : placeholder}
                    autoComplete="off"
                />
                {value && (
                    <button
                        type="button"
                        className="searchable-select__clear"
                        onClick={handleClear}
                        title="Clear selection"
                    >
                        ✕
                    </button>
                )}
                <span className="searchable-select__arrow">▼</span>
            </div>

            {isOpen && (
                <ul className="searchable-select__dropdown">
                    <li
                        className={`searchable-select__option searchable-select__option--empty ${!value && highlightedIndex === -1 ? 'searchable-select__option--highlighted' : ''
                            }`}
                        onClick={() => handleSelect('')}
                    >
                        -- No manual mapping --
                    </li>
                    {filteredOptions.length === 0 ? (
                        <li className="searchable-select__no-results">
                            No results found
                        </li>
                    ) : (
                        filteredOptions.map((opt, idx) => (
                            <li
                                key={opt.value}
                                className={`searchable-select__option ${String(opt.value) === String(value) ? 'searchable-select__option--selected' : ''
                                    } ${highlightedIndex === idx ? 'searchable-select__option--highlighted' : ''}`}
                                onClick={() => handleSelect(opt.value)}
                                onMouseEnter={() => setHighlightedIndex(idx)}
                            >
                                <span className="searchable-select__option-label">{opt.label}</span>
                                {opt.label !== String(opt.value) && (
                                    <span className="searchable-select__option-id">ID: {opt.value}</span>
                                )}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
}

export default SearchableSelect;
